/**
 * Supported locales configuration for Guest PWA
 * Used in chain and location PWA forms for multi-language content
 */
export const SUPPORTED_LOCALES = [
  {
    value: 'en',
    label: 'English',
    flag: '🇬🇧',
    welcomePlaceholder: 'Welcome to our hotel',
    descriptionPlaceholder: 'Describe your chain...',
  },
  {
    value: 'it',
    label: 'Italiano',
    flag: '🇮🇹',
    welcomePlaceholder: 'Benvenuto nel nostro hotel',
    descriptionPlaceholder: 'Descrivi la tua catena...',
  },
  {
    value: 'de',
    label: 'Deutsch',
    flag: '🇩🇪',
    welcomePlaceholder: 'Willkommen in unserem Hotel',
    descriptionPlaceholder: 'Beschreiben Sie Ihre Kette...',
  },
  {
    value: 'fr',
    label: 'Français',
    flag: '🇫🇷',
    welcomePlaceholder: 'Bienvenue dans notre hôtel',
    descriptionPlaceholder: 'Décrivez votre chaîne...',
  },
  {
    value: 'nl',
    label: 'Nederlands',
    flag: '🇳🇱',
    welcomePlaceholder: 'Welkom in ons hotel',
    descriptionPlaceholder: 'Beschrijf uw keten...',
  },
  {
    value: 'es',
    label: 'Español',
    flag: '🇪🇸',
    welcomePlaceholder: 'Bienvenido a nuestro hotel',
    descriptionPlaceholder: 'Describe tu cadena...',
  },
  {
    value: 'pl',
    label: 'Polski',
    flag: '🇵🇱',
    welcomePlaceholder: 'Witamy w naszym hotelu',
    descriptionPlaceholder: 'Opisz swoją sieć...',
  },
] as const

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]
export type LocaleCode = SupportedLocale['value']

/**
 * Locale info type for function returns (more flexible than SupportedLocale literal type)
 */
export interface LocaleInfo {
  value: string
  label: string
  flag: string
  welcomePlaceholder: string
  descriptionPlaceholder: string
}

/**
 * Default locale fallback
 */
export const DEFAULT_LOCALE: LocaleCode = 'en'

/**
 * Default locales if none provided
 */
export const DEFAULT_LOCALES: LocaleCode[] = [DEFAULT_LOCALE, 'it']

/**
 * Get locale info by code
 */
export const getLocaleInfo = (localeCode: string): LocaleInfo => {
  return (
    SUPPORTED_LOCALES.find((l) => l.value === localeCode) || {
      value: localeCode,
      label: localeCode.toUpperCase(),
      flag: '',
      welcomePlaceholder: '',
      descriptionPlaceholder: '',
    }
  )
}

/**
 * Resolve a translatable option (label can be a translations object or a plain string)
 * into a flat Option with a localized label string.
 */
export const localizeOption = (
  option: { value: string | number; label: Record<string, string> | string },
  locale: string
): { value: string; label: string } => ({
  value: String(option.value),
  label:
    typeof option.label === 'object'
      ? getLocalizedText(option.label, locale) ?? ''
      : option.label,
})

/**
 * Get localized text from a translations object.
 *
 * Lookup chain, in order:
 *   1. the active UI locale (`currentLocale`),
 *   2. the per-company fallback locale (`fallbackLocale`, DEV-666) — passed by
 *      callers that read from a payload carrying the company's `fallback_locale`,
 *   3. the global `DEFAULT_LOCALE` constant,
 *   4. the first non-empty translation in the map (legacy safety net),
 *   5. `null` when nothing matches.
 *
 * Empty strings are treated as missing so a deliberate blank in one locale
 * falls through to the next tier instead of rendering as an empty label.
 */
export const getLocalizedText = (
  translations: Record<string, string> | null | undefined,
  currentLocale: string,
  fallbackLocale?: string | null
): string | null => {
  if (!translations || typeof translations !== 'object') {
    return null
  }

  const candidates = [currentLocale, fallbackLocale, DEFAULT_LOCALE]
  for (const locale of candidates) {
    if (!locale) continue
    const value = translations[locale]
    if (value) return value
  }

  for (const value of Object.values(translations)) {
    if (value) return value
  }

  return null
}
